import {createAsyncThunk} from '@reduxjs/toolkit';
import type {
  ChangePlanCostV2,
  ChangePlanCostV2Args,
  ChangePlanDialogV2Response,
  ChangePlanSuccessResponse,
  ChangePlanV2Args,
  TeamInviteContext,
  ValidatePromoCodeResponse,
  ValidatePromoCodeV2Args,
} from '@Panels/change-plan-modal-v2/change-plan-modal-v2.types';
import type {TeamMember} from '@Components/team-members-list';
import {getPromoValidationErrorCode} from '@Libraries/change-subscription-plan-library';

interface ChangePlanEndpoints {
  init: string;
  cost: string;
  teamInviteContext: string;
}

const LIVE_ENDPOINTS: ChangePlanEndpoints = {
  init: 'premium/getChangeSubscriptionPlanDialog',
  cost: 'premium/getChangePlanCost',
  teamInviteContext: 'teams/getAddTeamMembersDialogData',
};

const PREVIEW_ENDPOINTS: ChangePlanEndpoints = {
  init: 'designsystem/changePlanDialogData',
  cost: 'designsystem/changePlanCostData',
  teamInviteContext: 'designsystem/changePlanTeamInviteContextData',
};

/**
 * The manageapp design-system preview reads stand-in payloads instead of the live ones. The live init
 * and cost endpoints are derived from the viewer's own subscription and error out for anyone who does
 * not have one; the live invite context answers from their own team and hides the invite link from
 * anyone who is not its admin, which would leave the team step reviewing as something no admin sees.
 */
function getEndpoints(isPreview: boolean): ChangePlanEndpoints {
  return isPreview ? PREVIEW_ENDPOINTS : LIVE_ENDPOINTS;
}

export const initChangePlanModalV2 = createAsyncThunk<ChangePlanDialogV2Response, boolean>('changePlanModalV2/init', async (isPreview) => {
  return (await window.PMW.readLocal(getEndpoints(isPreview).init)) as ChangePlanDialogV2Response;
});

export const getTeamMembers = createAsyncThunk<Record<string, TeamMember>>('changePlanModalV2/getTeamMembers', async () => {
  return (await window.PMW.readLocal('teams/getTeamMembersForAdmin')) as Record<string, TeamMember>;
});

export const getTeamInviteContext = createAsyncThunk<TeamInviteContext, boolean>('changePlanModalV2/getTeamInviteContext', async (isPreview) => {
  return (await window.PMW.readLocal(getEndpoints(isPreview).teamInviteContext)) as TeamInviteContext;
});

export const getChangePlanCost = createAsyncThunk<ChangePlanCostV2, ChangePlanCostV2Args>('changePlanModalV2/getChangePlanCost', async ({isPreview, ...selection}) => {
  return (await window.PMW.readLocal(getEndpoints(isPreview).cost, selection)) as ChangePlanCostV2;
});

export const changePlan = createAsyncThunk<ChangePlanSuccessResponse, ChangePlanV2Args>('changePlanModalV2/changePlan', async (data) => {
  return (await window.PMW.writeLocal('premium/changeSubscriptionPlan', data)) as ChangePlanSuccessResponse;
});

export const validatePromoCode = createAsyncThunk<ValidatePromoCodeResponse, ValidatePromoCodeV2Args, {rejectValue: string}>(
  'changePlanModalV2/validatePromoCode',
  async ({priceId, promoCode, seats, isPromoRemoved}, {rejectWithValue}) => {
    try {
      return (await window.PMW.readLocal('premium/isValidPromotion', {priceId, promoCode, seats, isPromoRemoved, isSeatPurchase: 0})) as ValidatePromoCodeResponse;
    } catch (error: unknown) {
      return rejectWithValue(getPromoValidationErrorCode(error));
    }
  }
);
